import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SearchSelect from '../components/shared/SearchSelect';
import { FilePicker, FormModal, Field, LockTip, inputCls, lockedInputCls, btnPrimary, btnGhost, grid2, grid3 } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import {
  engagementToFields, fieldsToEngagementValues,
  engagementRequiredGaps, EngagementDateFields, EngagementProfileFields,
  EngagementPeopleFields, EngagementScreeningQuestion, PEOPLE_SECTION_HINT,
  effectiveServiceOf, isMultiFyeService, companiesOf, fieldsForCompany, slotQuestionOf,
  activeServiceLines, serviceLineClashes,
} from '../components/shared/EngagementFormFields';
import { composeEngagementName } from '../utils/engagementName';
import { lineFeeKey, effectiveService as lineServiceOf, serviceLineText } from '../utils/serviceLines';
import {
  useBizFiles, BizFileUploads, useDuplicateEngagements, useEngagementSlotCheck, DuplicateEngagementWarning, DescriptionOfServiceNotice, descriptionNeededFrom,
  CompanyChecklist, COMPANIES_SECTION_HINT, companyRequiredGaps, feeRowsOf,
} from '../components/shared/EngagementCompanies';
import { EngagementHistory, LinkedQcSection, LinkedRecordsSection } from '../components/shared/EngagementView';
import { AttachmentsEditor } from './QMEngagements';
import { fetchJSON } from '../utils/api';
import { formatDate as fmtDate } from '../utils/dateFormat';

/**
 * Management → Engagements → New / Edit.
 *
 * Built on the SHARED engagement field set
 * (components/shared/EngagementFormFields.js): this form, WORKFLOW →
 * Engagement Registration and WORKFLOW → Engagement Amendment must ask the
 * same questions on the same page layout, with Registration as the reference.
 *
 *   - the client-profile questions (client type, TNA, TNE, fees, SSIC codes,
 *     principal activities, overseas ops, consolidated FS, SEC, date of
 *     appointment) are asked here, not only on the request;
 *   - Partner / Director / Manager are multi-select id lists — a single-name
 *     select could not even express a second director, and saving would
 *     replace the role with the one name shown, quietly dropping an
 *     engagement's other leads (and their place in the QC / FS / QC35
 *     reviewer pool);
 *   - New is a FormShell page with numbered sections, like the two workflow
 *     forms, instead of a modal floating over an empty page.
 *
 * New vs Edit differ only where they must (docs/conventions.md §2): create is
 * the lean form, so sign-off / archival / CaseWare / charge code / status /
 * attachments / history are Edit-only — they are post-creation lifecycle state.
 *
 * The Company section is the one block the two modes cannot share. New picks
 * companies the way Engagement Registration does (shared/EngagementCompanies.js:
 * client group → tick every company → one engagement each, one BizFile per
 * company, the live "one per company per FYE" warning). Edit is one existing
 * engagement — a single entity link, kept editable for legacy rows that have
 * none.
 */

// Dates arrive as YYYY-MM-DD or full ISO strings — keep the date part only.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');
// 這頁不算歸檔期限的預設：沒填就是空的，後端讀取時用「簽署日 + 50」
// 衍生。填了才是人工覆寫。

const emptyForm = (engagement, options) => ({
  ...engagementToFields(engagement, options),
  clientGroup: engagement?.clientGroup || '',
  entityId: engagement?.entityId ?? null,
  entityName: engagement?.entityName || '',
  // New only — the Registration shape (group id + ticked company ids).
  groupId: '', entityIds: [],
  engagementName: engagement?.engagementName || '',
  // Lifecycle state — Edit only.（signOffDate 不在表單裡：唯讀；
  // archivalDate 同理：由連結的 QC35 記錄講，這頁只顯示不收。）
  status: engagement?.status || '',
  archivalDeadline: toDateInput(engagement?.archivalDeadline),
  casewareOpened: engagement?.casewareOpened || false,
  chargeCodeAssigned: engagement?.chargeCodeAssigned || false,
});

/**
 * Everything the form needs, in the same envelope the two workflow forms get
 * (/api/workflow/engreg/options). One shape → one set of field components.
 */
const useFormOptions = (engagement) => {
  const [options, setOptions] = useState({
    partners: [], directors: [], managers: [], staff: [],
    services: [], statuses: [],
    groups: [], entities: [],
  });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON('/api/engagements/form-options');
        if (!cancelled) setOptions((prev) => ({ ...prev, ...data }));
      } catch (e) {
        console.error('Error loading engagement form options:', e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [engagement]);
  return [options, loaded];
};

// ── The shared body ─────────────────────────────────────────────────────────

// No return-comment layer here, so the shared blocks key their controls by
// their canonical names — the same keys engagementRequiredGaps reports, which
// is what lets a Submit mark the very control that is empty.
const sameKey = (k) => k;

const EngagementFormBody = ({ f, set, setForm, options, engagement, isEdit, onAttachmentsChanged, onDuplicates }) => {
  const { hasPermission } = useAuth();
  const canManageLists = hasPermission('settings:lists:edit');
  const canManageStaff = hasPermission('qm:staff:edit');
  const { groups = [], entities = [], statuses = [] } = options;

  // The duplicate guard lives in the body, like Registration's, so neither
  // page can render the form without it; the page is only told, to stop a
  // submit already known to fail. New asks for the ticked companies; Edit asks
  // once the engagement moves to another company / FYE / service or its
  // description changes (the engagement itself never counts).
  const slot = slotQuestionOf(f);
  const newCheck = useDuplicateEngagements('/api/engagements/duplicate-check', isEdit ? [] : slot.sharedIds, f.yearEnd, null,
    { service: f.service, serviceOther: f.serviceOther, description: f.descriptionOfService, rows: isEdit ? [] : slot.rows });
  const editCheck = useEngagementSlotCheck('/api/engagements/duplicate-check', isEdit ? engagement : null, f);
  const { duplicates, notices } = isEdit ? editCheck : newCheck;
  // New: the slots the form itself fills twice (a company's own service
  // against its lines) block the submit like a live-check clash.
  const companies = isEdit ? [] : companiesOf(f, options);
  const formClashes = useMemo(
    () => (isEdit ? [] : serviceLineClashes(f, { companies, multiFyeServices: options.multiFyeServices || [] })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, f.entityIds, f.serviceLines, f.service, f.serviceOther, f.descriptionOfService, f.profileSplit, options]
  );
  const blocking = useMemo(() => [...duplicates, ...formClashes], [duplicates, formClashes]);
  useEffect(() => { if (onDuplicates) onDuplicates(blocking); }, [blocking, onDuplicates]);

  const selectedGroup = groups.find((g) => g.name === f.clientGroup) || null;
  const selectedGroupId = selectedGroup ? selectedGroup.id : null;

  const handleGroupChange = (name) => set('clientGroup', name);

  const handleEntityChange = (v) => {
    if (v === '') { setForm((prev) => ({ ...prev, entityId: null, entityName: '' })); return; }
    if (v === '__legacy__') {
      // Keep the unlinked legacy text as-is.
      setForm((prev) => ({ ...prev, entityId: null, entityName: engagement?.entityName || prev.entityName }));
      return;
    }
    const entity = entities.find((en) => String(en.id) === String(v));
    if (!entity) return;
    setForm((prev) => {
      const next = { ...prev, entityId: entity.id, entityName: entity.entityName };
      if (!prev.clientGroup && entity.clientGroupName) next.clientGroup = entity.clientGroupName;
      return next;
    });
  };

  // Entity choices: filter by the chosen group, but keep the currently linked
  // entity visible even when it falls outside the filter.
  const entityChoices = entities.filter((en) =>
    !selectedGroupId || en.clientGroupId === selectedGroupId || en.id === f.entityId);
  const linkedEntity = f.entityId ? entities.find((en) => en.id === f.entityId) : null;
  const linkedUen = linkedEntity ? linkedEntity.uen : (f.entityId ? engagement?.uen : null);
  const entitySelectValue = f.entityId ? String(f.entityId) : (f.entityName ? '__legacy__' : '');

  // Entity-linked engagements carry a composed name — preview it and lock the
  // field. Legacy rows (edit, no entity link) keep the free-text name. Same
  // rule as the server (utils/engagementName): service tail, and for a service
  // that allows several engagements per company and FYE the number from the
  // second one on — kept while the slot stays the same, otherwise assigned on
  // save (the live duplicate-check says whether the slot is taken).
  const previewService = effectiveServiceOf(f) || null;
  const multi = isMultiFyeService(f, options.multiFyeServices || []);
  const sameSlot = isEdit && engagement && engagement.entityId === f.entityId && engagement.yearEnd === f.yearEnd
    && String(engagement.serviceProvided || '').toLowerCase() === String(previewService || '').toLowerCase();
  const previewSeq = !multi ? null
    : sameSlot && engagement.engagementSeq ? engagement.engagementSeq
      : (!isEdit && descriptionNeededFrom(duplicates, notices)) ? '…' : null;
  const composedName = f.entityId && linkedUen && f.yearEnd
    ? composeEngagementName((linkedEntity?.entityName || f.entityName).trim(), String(linkedUen).trim().toUpperCase(), f.yearEnd, previewService, previewSeq)
    : null;
  const nameLocked = !!f.entityId;

  const manageClients = canManageLists && (
    <Link to="/settings/clients" className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">Manage Clients</Link>
  );
  const manageLists = canManageLists && (
    <Link to="/settings/lists" className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">Manage Lists</Link>
  );

  return (
    <>
      {!isEdit && (
        <FormSection step={1} title="Companies" hint={COMPANIES_SECTION_HINT}>
          <CompanyChecklist f={f} set={set} options={options} />
          <EngagementDateFields f={f} set={set} required anchor={sameKey} />
          {/* Directly under the two inputs that decide it — the answer to
              "companies × FYE", read where the question was asked. */}
          <DuplicateEngagementWarning duplicates={duplicates} />
          <p className="text-xs text-slate-500">Each engagement is named automatically: Entity Name - UEN - FYE - Service; when the service allows several engagements per company and FYE, the second one on is numbered (#2, #3…).</p>
        </FormSection>
      )}
      {isEdit && (
      <FormSection
        step={1}
        title="Company"
        hint="One engagement per company per FYE."
      >
        <div className={grid2}>
          <Field label="Client Group" action={manageClients}>
            <SearchSelect
              value={f.clientGroup}
              onChange={handleGroupChange}
              options={[
                ...groups.map((g) => ({ value: g.name, label: g.name })),
                ...(f.clientGroup && !groups.some((g) => g.name === f.clientGroup)
                  ? [{ value: f.clientGroup, label: f.clientGroup }] : []),
              ]}
            />
          </Field>
          <Field
            label="Entity Name"
            required={!isEdit}
            field="entityId"
            action={manageClients}
          >
            <SearchSelect
              value={entitySelectValue}
              onChange={handleEntityChange}
              options={[
                ...entityChoices.map((en) => ({ value: en.id, label: `${en.entityName}${en.uen ? ` (${en.uen})` : ' (no UEN)'}` })),
                ...(f.entityId && !entities.some((en) => en.id === f.entityId)
                  ? [{ value: f.entityId, label: engagement?.uen ? `${f.entityName} (${engagement.uen})` : f.entityName }] : []),
                ...(!f.entityId && f.entityName
                  ? [{ value: '__legacy__', label: f.entityName }] : []),
              ]}
              placeholder="Search entity…"
            />
            {f.entityId && linkedUen && <p className="text-xs text-slate-500 mt-1">UEN: {linkedUen}</p>}
            {!f.entityId && f.entityName && (
              <p className="text-xs text-slate-500 mt-1">Select an entity to link this record</p>
            )}
          </Field>
        </div>
        {/* Name comes after the dates: it is composed from Entity + UEN + FYE,
            so it can only be shown once the FYE has been picked. The Date of
            Appointment is the client master's — shown, never edited here. */}
        <EngagementDateFields
          f={f} set={set} required={false} anchor={sameKey}
          appointment={isEdit ? { date: engagement?.dateOfAppointment || null, source: engagement?.dateOfAppointmentSource || null } : undefined}
        />
        <Field
          label="Engagement Name"
          required={!nameLocked}
          field="engagementName"
          hint={nameLocked ? 'Composed from Entity Name - UEN - FYE — it updates when those change.' : undefined}
        >
          {nameLocked ? (
            <input type="text" value={composedName || f.engagementName || ''} readOnly className={lockedInputCls} />
          ) : (
            <input type="text" value={f.engagementName} onChange={(e) => set('engagementName', e.target.value)} className={inputCls} />
          )}
        </Field>
      </FormSection>
      )}

      <FormSection step={2} title="Service & Client Profile">
        <EngagementProfileFields
          f={f} set={set} options={options} required={!isEdit} anchor={sameKey} feesLocked={!!(isEdit && engagement?.qcRecordId)}
          feeCompanies={isEdit ? [] : feeRowsOf(f, options)}
          companies={companies}
          serviceLines={!isEdit} duplicates={duplicates} notices={notices}
          descriptionNeeded={descriptionNeededFrom(duplicates, notices)}
          descriptionNotice={(
            <>
              {/* Edit has no company checklist to stand under — the slot
                  warning goes right under Service Provided. */}
              {isEdit && <DuplicateEngagementWarning duplicates={duplicates} />}
              <DescriptionOfServiceNotice duplicates={duplicates} notices={notices} />
            </>
          )}
        />
        {isEdit && (
          <Field label="Status" field="status" action={manageLists} hint="Normally maintained by the flows themselves — a manual change with a reviewer goes through Engagement Status Change.">
            <select value={f.status} onChange={(e) => set('status', e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              {f.status && !statuses.includes(f.status) && <option value={f.status}>{f.status}</option>}
            </select>
          </Field>
        )}
      </FormSection>

      <FormSection
        step={3}
        title="People"
        hint={PEOPLE_SECTION_HINT}
      >
        <div className="flex justify-end -mt-1">
          {canManageStaff && (
            <Link to="/qm/staff" className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">Manage Staff</Link>
          )}
        </div>
        <EngagementPeopleFields f={f} set={set} options={options} required={false} anchor={sameKey} />
      </FormSection>

      <FormSection step={4} title="Remarks">
        <Field label="Remarks">
          <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
        </Field>
      </FormSection>

      {/* Post-creation lifecycle state — Edit only (docs/conventions.md §2). */}
      {isEdit && (
        <FormSection step={5} title="Lifecycle & Documents">
          {/* 簽署日唯讀——它是**連結的 Signed Financial Statements 記錄**講的，不是這案
              自己的欄位。
              歸檔日同理——它是**連結的 QC35 記錄**講的（QC35 核准＝歸檔
              完成），要更正走 QC35 Form Amendment 或記錄頁。歸檔期限仍可改，改了就是
              人工覆寫；沒改過的則跟著簽署日自動移動（天數依 qm_archival_deadline_rules 的期間）。 */}
          <div className={grid3}>
            <Field label="Sign Off Date">
              <LockTip text="Comes from the linked Signed Financial Statements record." block>
                <input type="text" value={engagement?.signOffDate ? fmtDate(engagement.signOffDate) : '—'} readOnly className={lockedInputCls} />
              </LockTip>
              <p className="text-xs text-slate-500 mt-1">
                {engagement?.fsRecordId
                  ? <>From the linked <RefLink to={`/qm/records/fs/${engagement.fsRecordId}`} className="text-blue-600 hover:text-blue-800">Signed Financial Statements record</RefLink>.</>
                  : 'Appears once this engagement has a Signed Financial Statements record.'}
              </p>
            </Field>
            <Field label="Archival Deadline" hint="Blank follows the sign-off date plus the number of days set for the period it falls in; a value here overrides it.">
              <input type="date" value={f.archivalDeadline} onChange={(e) => set('archivalDeadline', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Archival Date">
              <LockTip text="Comes from the linked QC35 Form record." block>
                <input type="text" value={engagement?.archivalDate ? fmtDate(engagement.archivalDate) : '—'} readOnly className={lockedInputCls} />
              </LockTip>
              <p className="text-xs text-slate-500 mt-1">
                {engagement?.qc35RecordId
                  ? <>From the linked <RefLink to={`/qm/records/qc35/${engagement.qc35RecordId}`} className="text-blue-600 hover:text-blue-800">QC35 Form record</RefLink>.</>
                  : 'Appears once this engagement has a QC35 Form record — QC35 Form approval is the archival.'}
              </p>
            </Field>
          </div>
          <div className={grid2}>
            {/* Charge Code 與 CaseWare 一樣只記「有／沒有」——要的是
                狀態，不是那串碼，所以兩格是同一種勾選。 */}
            <Field label="CaseWare">
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-2.5">
                <input type="checkbox" checked={!!f.casewareOpened} onChange={(e) => set('casewareOpened', e.target.checked)} />
                CaseWare activated for this engagement
              </label>
            </Field>
            <Field label="Charge Code">
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-2.5">
                <input type="checkbox" checked={!!f.chargeCodeAssigned} onChange={(e) => set('chargeCodeAssigned', e.target.checked)} />
                Charge code activated for this engagement
              </label>
            </Field>
          </div>
          {/* The screening question is asked HERE and not in §2:
              it belongs with the document it governs. Edit asks it
              — New does not, because a directly-created engagement never went
              past QM, so there is nothing to answer, only documents to file. */}
          <EngagementScreeningQuestion f={f} set={set} required={false} anchor={sameKey} />
          {engagement?.qcRecordId && <LinkedQcSection basePath={`/api/engagements/${engagement.id}`} />}
          {/* FS 與 QC35 也是引用，所以跟 QC 一樣在這裡讀得到記錄本身。 */}
          <LinkedRecordsSection basePath={`/api/engagements/${engagement.id}`} engagementId={engagement.id} />
          <AttachmentsEditor engagementId={engagement.id} onChanged={onAttachmentsChanged} />
        </FormSection>
      )}
    </>
  );
};

// The PATCH body: the shared field values plus this surface's own keys.
const buildBody = (f, options, { isEdit, nameLocked }) => ({
  ...fieldsToEngagementValues(f, options),
  clientGroup: String(f.clientGroup || '').trim() || null,
  entityId: f.entityId ?? null,
  entityName: String(f.entityName || '').trim() || null,
  // Entity-linked rows get their name composed server-side (Entity - UEN - FYE).
  ...(nameLocked ? {} : { engagementName: String(f.engagementName || '').trim() }),
  ...(isEdit ? {
    // Creation does not ask for a status: the route stamps the
    // configured creation status, the same source an approval reads.
    status: String(f.status || '').trim(),
    // signOffDate 不送——它由連結的 Signed Financial Statements 記錄講，這條路徑寫不到。
    // archivalDate 同理（連結的 QC35 記錄講），不送。
    archivalDeadline: f.archivalDeadline || null,
    casewareOpened: !!f.casewareOpened,
    chargeCodeAssigned: !!f.chargeCodeAssigned,
  } : {}),
});

// Every required control still empty → `[{ key, label }]`, key = the control's
// `field=` (this surface's own keys first, then the shared blocks' canonical
// keys), label = the wording on screen. The page hands it to formValidation.
const requiredGaps = (f, { isEdit, multiFyeServices = [] }) => {
  const gaps = isEdit ? [] : companyRequiredGaps(f);
  if (isEdit && !f.entityId && !String(f.engagementName || '').trim()) gaps.push({ key: 'engagementName', label: 'Engagement Name' });
  if (isEdit && !String(f.status || '').trim()) gaps.push({ key: 'status', label: 'Status' });
  // Creation asks the full Registration question set (the
  // three surfaces ask the same thing). Editing does not — legacy engagements
  // have no answers, and demanding them would lock every
  // legacy record out of every edit. The one exception on Edit is the
  // Description of Service of a service that allows several engagements per
  // company and FYE (engagementRequiredGaps applies it on every surface).
  // People: no single lead is required, only at least one person across the
  // three leads and the team.
  gaps.push(...engagementRequiredGaps(f, { requireProfile: !isEdit, multiFyeServices }));
  if ([f.partnerStaffIds, f.directorStaffIds, f.managerStaffIds, f.teamStaffIds].every((ids) => (ids || []).length === 0)) {
    gaps.push({ key: 'partnerStaffIds', label: 'Partner in Charge, Director(s), Manager(s) or Team Member(s) (At Least One)' });
  }
  return gaps;
};

// ── New: a FormShell page, like the two workflow forms ──────────────────────

/**
 * Documents on the New page.
 *
 * Same three kinds Engagement Registration asks for, in the same order —
 * one BizFile per ticked company, the Screening document, then free-form
 * attachments. Two deliberate differences:
 *
 *   - **no screening question**: this form writes the engagement directly, with
 *     no QM review in between, so "does QM need to do it" has nobody to ask.
 *     It just files whatever documents exist.
 *   - **nothing is mandatory**: this surface writes LIVE records, and Management
 *     also uses it to enter historical engagements whose paperwork may never
 *     appear. The draft-side "you cannot submit without it" rule has no
 *     equivalent here.
 *
 * Where they land: each BizFile on its own company's engagement; the Screening
 * document and the attachments on EVERY engagement created — they describe the
 * project, not one company, and a registration's are visible from each covered
 * engagement the same way.
 */
const SUPPORTING_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png';

// The POST body of one company: the shared answers plus that company's link.
// The group name is copied as of today (docs/data-model.md §2 — snapshots).
// Several companies (`groupKey` set): every company carries its own amount
// and the submission's key; the answers it gives on its own ("Set per
// Company") replace the shared ones (fieldsForCompany).
const newEngagementBody = (f, options, entity, groupKey = null) => {
  const own = groupKey ? ((f.proposedFeesSplit || {})[entity.id]) : undefined;
  const hasOwn = own !== undefined && own !== null && String(own).trim() !== '';
  return {
    ...fieldsToEngagementValues(groupKey ? fieldsForCompany(f, entity.id) : f, options),
    ...(groupKey ? {
      ...(hasOwn ? { proposedFees: own } : { proposedFeesShared: true }),
      proposedFeesGroupKey: groupKey,
    } : {}),
    clientGroup: entity.clientGroupName
      || ((options.groups || []).find((g) => String(g.id) === String(f.groupId)) || {}).name
      || null,
    entityId: entity.id,
    entityName: entity.entityName,
  };
};

// A service line's POST body: its company's own body with the line's service,
// Description of Service and fee row (one more engagement under the company).
const serviceLineEngagementBody = (f, options, entity, line, groupKey) => {
  const body = newEngagementBody(f, options, entity, groupKey);
  const own = (f.proposedFeesSplit || {})[lineFeeKey(line.k)];
  const hasOwn = own !== undefined && own !== null && String(own).trim() !== '';
  delete body.proposedFeesShared;
  return {
    ...body,
    serviceProvided: lineServiceOf(line.service, line.serviceOther) || null,
    descriptionOfService: String(line.descriptionOfService || '').trim() || null,
    ...(hasOwn ? { proposedFees: own } : { proposedFees: fieldsToEngagementValues(f, options).proposedFees, proposedFeesShared: true }),
  };
};

export const EngagementNewPage = () => {
  const navigate = useNavigate();
  const [options] = useFormOptions(null);
  // A blank form needs no options to initialise (there is nothing to convert),
  // so the page renders straight away and the dropdowns fill in.
  const [f, setForm] = useState(() => emptyForm(null, {}));
  const [bizFiles, setBizFiles] = useBizFiles(f.entityIds);
  const [screeningFile, setScreeningFile] = useState(null);
  const [supporting, setSupporting] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Each document is posted to the existing attachment endpoint — the same one
  // the Edit panel uses, so an upload here and an upload later are the same
  // row with the same rules. Returns the labels that did not upload.
  const uploadDocs = async (created) => {
    const queue = [];
    for (const { entity, engagement } of created) {
      if (bizFiles[entity.id]) queue.push({ engagementId: engagement.id, file: bizFiles[entity.id], docType: 'BIZFILE', label: `BizFile — ${entity.entityName}` });
      if (screeningFile) queue.push({ engagementId: engagement.id, file: screeningFile, docType: 'SCREENING', label: `Screening document — ${entity.entityName}` });
      for (const file of supporting) queue.push({ engagementId: engagement.id, file, docType: 'SUPPLEMENTARY', label: `${file.name} — ${entity.entityName}` });
    }
    const failed = [];
    for (const item of queue) {
      const body = new FormData();
      body.append('file', item.file);
      body.append('docType', item.docType);
      try {
        await fetchJSON(`/api/engagements/${item.engagementId}/attachments`, { method: 'POST', body });
      } catch (e) { failed.push(`${item.label} (${e.message})`); }
    }
    return failed;
  };

  // 空白的必填格：按 Create 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => requiredGaps(f, { isEdit: false, multiFyeServices: options.multiFyeServices || [] }), [f, options]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    // Already known to fail — the same sentence the POST would refuse with.
    if (duplicates.length > 0) { setError(duplicates[0].message); return; }
    setSaving(true); setError(null);
    try {
      // One engagement per ticked company, all from the same answers — the
      // same fan-out a Registration approval does. A company that fails (a
      // clash that appeared since the warning, say) does not block the others;
      // the page says which ones were not created.
      const entities = (options.entities || []).filter((en) => f.entityIds.includes(en.id));
      const lines = activeServiceLines(f);
      const groupKey = entities.length + lines.length > 1 ? crypto.randomUUID() : null;
      const created = [];
      const failedCompanies = [];
      for (const entity of entities) {
        try {
          const resp = await fetchJSON('/api/engagements', {
            method: 'POST', body: JSON.stringify(newEngagementBody(f, options, entity, groupKey)),
          });
          if (resp.engagement && resp.engagement.id) created.push({ entity, engagement: resp.engagement });
        } catch (e) { failedCompanies.push(`${entity.entityName} (${e.message})`); }
      }
      // The additional services: one more engagement per line, under its
      // company, with the line's service, description and fee row. After the
      // companies' own engagements, so a repeated service numbers in form order.
      for (const line of lines) {
        const entity = entities.find((en) => Number(en.id) === Number(line.entityId));
        if (!entity) continue;
        try {
          const resp = await fetchJSON('/api/engagements', {
            method: 'POST', body: JSON.stringify(serviceLineEngagementBody(f, options, entity, line, groupKey)),
          });
          if (resp.engagement && resp.engagement.id) created.push({ entity, engagement: resp.engagement, line: true });
        } catch (e) { failedCompanies.push(`${entity.entityName} — ${serviceLineText(line)} (${e.message})`); }
      }
      if (created.length === 0) {
        setError(`No engagement was created: ${failedCompanies.join('; ')}`);
        setSaving(false);
        return;
      }

      const failedDocs = await uploadDocs(created);
      // The engagements exist either way — say so instead of navigating away
      // and leaving the gaps to be discovered later.
      if (failedCompanies.length > 0 || failedDocs.length > 0) {
        const parts = [];
        if (failedCompanies.length > 0) parts.push(`these companies were not created: ${failedCompanies.join('; ')}`);
        if (failedDocs.length > 0) parts.push(`these documents did not upload: ${failedDocs.join('; ')} — add them from the engagement's Edit panel`);
        setError(`Engagement${created.length > 1 ? 's' : ''} created, but ${parts.join('; and ')}.`);
        setSaving(false);
        return;
      }
      // Several created ⇒ the list (the whole batch at once); one ⇒ its detail page.
      navigate(created.length > 1 ? '/qm/engagements' : `/qm/engagements/${created[0].engagement.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/qm/engagements"
      backLabel="Engagements"
      title="New Engagement"
      subtitle="Create engagements directly, without review."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitLabel="Create Engagement"
    >
      <FormValidationProvider value={validation.providerValue}>
      <EngagementFormBody f={f} set={set} setForm={setForm} options={options} engagement={null} isEdit={false} onDuplicates={setDuplicates} />

      {/* Same documents, same order as Engagement Registration —
          BizFile per company, Screening, then supporting material. */}
      <FormSection
        step={5}
        title="BizFile"
        hint="The ACRA business profile of each company — one PDF per company; it can be added later from the Edit panel."
      >
        <BizFileUploads f={f} options={options} bizFiles={bizFiles} setBizFiles={setBizFiles} required={false} />
      </FormSection>
      <FormSection
        step={6}
        title="Documents"
        hint="Filed on every engagement created above; documents can be added later from the Edit panel."
      >
        <Field label="Screening Document" hint="Background-check document. PDF only, up to 30 MB.">
          <FilePicker accept=".pdf"
            onChange={(e) => setScreeningFile((e.target.files || [])[0] || null)}
            files={screeningFile} />
        </Field>
        <Field label="Attachments" hint="PDF / DOC / DOCX / XLS / XLSX / JPG / PNG, up to 30 MB per file.">
          <FilePicker multiple accept={SUPPORTING_ACCEPT}
            onChange={(e) => setSupporting(Array.from(e.target.files || []))}
            files={supporting} />
        </Field>
      </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

// ── Edit: the same body in a modal, opened from the detail page ─────────────

export const EngagementModal = ({ engagement, onClose, onSaved, onAttachmentsChanged }) => {
  const [options, loaded] = useFormOptions(engagement);
  // Prefilling needs the options: the managed service list decides whether the
  // stored service text is one of the choices or the free text an 'Other'
  // answer collapsed into. So the form state is built once they arrive.
  const [f, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (loaded) setForm((prev) => prev || emptyForm(engagement, options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消（表單載入前沒有欄位可標）。
  const gaps = useMemo(() => (f ? requiredGaps(f, { isEdit: true, multiFyeServices: options.multiFyeServices || [] }) : []), [f, options]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (duplicates.length > 0) { setError(duplicates[0].message); return; }
    setSaving(true); setError(null);
    try {
      await fetchJSON(`/api/engagements/${engagement.id}`, {
        method: 'PATCH',
        body: JSON.stringify(buildBody(f, options, { isEdit: true, nameLocked: !!f.entityId })),
      });
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <FormModal
      title={`Edit ${engagement.engagementName}`}
      size="xl"
      onClose={onClose}
      bodyClassName="bg-slate-50"
      footer={(
        <>
          <button onClick={onClose} className={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving || !f} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      {!f ? <p className="text-sm text-slate-500">Loading…</p> : (
      <FormValidationProvider value={validation.providerValue}>
      <EngagementFormBody
        f={f}
        set={set}
        setForm={setForm}
        options={options}
        engagement={engagement}
        isEdit
        onAttachmentsChanged={onAttachmentsChanged}
        onDuplicates={setDuplicates}
      />

      {/* Full change history in place — collapsed by default. */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-slate-200 bg-slate-100 rounded-t-[7px] text-left"
        >
          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
          <span className="text-sm font-medium text-slate-700">History</span>
        </button>
        {showHistory && (
          <div className="p-4">
            <EngagementHistory basePath={`/api/engagements/${engagement.id}`} />
          </div>
        )}
      </div>

      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
      </FormValidationProvider>
      )}
    </FormModal>
  );
};

export default EngagementNewPage;
